import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { Panel } from "../../components/panel/panel.jsx";
import Loader from "../../components/Loader.tsx";
import Button from "../../components/Button.tsx";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import {
  getLoanList,
  LoanRecord,
} from "../../services/loans.service.ts";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import FilterLoans from "../../components/loans/FilterLoans.tsx";
import PaginationLoans from "../../components/loans/PaginationLoans.tsx";
import {
  setRecords,
  setTotal,
  setPage,
  setSortBy,
  setDefaultSort,
} from "../../store/slices/loansSlice.ts";
import { fixedHeadStyle, fixedStyle } from "../../utils/constanst.js";

interface LoanParams {
  limit: number;
  offset: number;
  param: string;
  sort: string;
  order: string;
  partnerId: string;
}

const SORTABLE_COLUMNS = {
  ID: "id",
  PARTNER: "partner",
  REQUEST_AMOUNT: "requestAmount",
  AMOUNT_DISCOUNT: "amountDiscount",
  START_DAY_PAY: "startDayPay",
  END_DAY_PAY: "endDayPay",
  STATUS: "approved",
} as const;

const StatusBadge = ({ record }: { record: LoanRecord }) => {
  if (record.approved) {
    return <span className="badge bg-success">APROBADO</span>;
  }

  if (record.isRejected) {
    return <span className="badge bg-danger">RECHAZADO</span>;
  }

  return <span className="badge bg-warning">PENDIENTE</span>;
};

const AllLoans = () => {
  // Configurar locale de dayjs
  dayjs.locale("es");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  useAppSettings();

  const {
    records,
    total,
    page,
    limit,
    query,
    sortBy,
    defaultSort,
  } = useSelector((state: RootState) => state.loans);

  const loanParams = useMemo((): LoanParams => {
    const offset = page * limit;
    const param = query.includes("param=")
      ? query.split("param=")[1]?.split("&")[0] || ""
      : "";
    const partnerId = query.includes("partnerId=")
      ? query.split("partnerId=")[1]?.split("&")[0] || ""
      : "";

    return {
      limit,
      offset,
      param,
      sort: sortBy,
      order: defaultSort.toUpperCase(),
      partnerId,
    };
  }, [page, limit, query, sortBy, defaultSort]);

  const fetchLoans = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await getLoanList({
        limit: loanParams.limit,
        offset: loanParams.offset,
        order: loanParams.order as "ASC" | "DESC",
        sort: loanParams.sort,
        param: loanParams.param,
        partnerId: loanParams.partnerId
          ? Number(loanParams.partnerId)
          : undefined,
      });
      dispatch(setRecords(response.data || []));
      dispatch(setTotal(response.total || 0));
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Error desconocido al cargar préstamos";
      setError(errorMessage);
      console.error("Error fetching loans:", err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, loanParams]);

  const handleSort = useCallback(
    (column: string): void => {
      const sortOrder =
        sortBy !== column ? "asc" : defaultSort === "asc" ? "desc" : "asc";

      dispatch(setSortBy(column));
      dispatch(setPage(0));
      dispatch(setDefaultSort(sortOrder));
    },
    [dispatch, sortBy, defaultSort]
  );


  useEffect(() => {
    fetchLoans();
  }, [fetchLoans]);

  const getIconSort = () => {
    return (
      <i
        className={`fa-solid fa-sort-${defaultSort === "asc" ? "up" : "down"}`}
      />
    );
  };

  const defaultStyle = { cursor: "pointer" };

  const formatDate = (value: any): string => {
    if (value === null || value === undefined || value === "") return "-";

    try {
      if (typeof value === "number") {
        const date = dayjs.unix(value);
        return date.isValid() ? date.format("DD/MM/YYYY") : "-";
      }

      if (typeof value === "string") {
        if (!isNaN(Number(value))) {
          const date = dayjs.unix(Number(value));
          return date.isValid() ? date.format("DD/MM/YYYY") : "-";
        }

        const date = dayjs(value);
        return date.isValid() ? date.format("DD/MM/YYYY") : "-";
      }

      const date = dayjs(value);
      return date.isValid() ? date.format("DD/MM/YYYY") : "-";
    } catch (error) {
      return "-";
    }
  };

  return (
    <Layout>
      <Title
        baseTitle="Préstamos"
        basePath="/prestamos"
        title="Listado de préstamos"
      />

      <div className="card border-0 m-4">
        <div className="tab-pane fade show active" id="sucursal">
          <div className="card-body">
            <Panel>
              <div className="input-group w-100">
                <div className="d-flex align-items-center w-100">
                  <FilterLoans />
                </div>
              </div>

              <div
                className="table-responsive mb-3"
                style={{ overflowY: "auto", height: "500px" }}
              >
                <table className="table table-hover table-panel text-nowrap align-middle mb-0">
                  <thead>
                    <tr style={{ ...fixedHeadStyle } as React.CSSProperties}>
                      <th
                        style={{ ...fixedStyle } as React.CSSProperties}
                        className="text-center"
                        onClick={() => handleSort("id")}
                      >
                        Id {sortBy === "id" && getIconSort()}
                      </th>
                      <th
                        className="text-center"
                        onClick={() => handleSort("partner")}
                      >
                        Colaborador {sortBy === "partner" && getIconSort()}
                      </th>
                      <th
                        className="text-center"
                        onClick={() => handleSort("requestAmount")}
                      >
                        Monto solicitado{" "}
                        {sortBy === "requestAmount" && getIconSort()}
                      </th>
                      <th
                        className="text-center"
                        onClick={() => handleSort("amountDiscount")}
                      >
                        Monto por quincena{" "}
                        {sortBy === "amountDiscount" && getIconSort()}
                      </th>
                      <th
                        className="text-center"
                        onClick={() => handleSort("startDayPay")}
                      >
                        Inicio pago{" "}
                        {sortBy === "startDayPay" && getIconSort()}
                      </th>
                      <th
                        className="text-center"
                        onClick={() => handleSort("endDayPay")}
                      >
                        Fin pago{" "}
                        {sortBy === "endDayPay" && getIconSort()}
                      </th>
                      <th
                        className="text-center"
                        onClick={() => handleSort("approved")}
                      >
                        Estado{" "}
                        {sortBy === "approved" && getIconSort()}
                      </th>

                      <th className="text-end">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records?.length > 0 ? (
                      records.map((record) => (
                        <tr key={record.id}>
                          <td
                            style={{ ...fixedStyle } as React.CSSProperties}
                            className="text-center"
                          >
                            {record.id}
                          </td>
                          <td className="text-center">
                            {record.partner?.person
                              ? toCamelCase(
                                  `${record.partner.person.firstName || ""} ${
                                    record.partner.person.lastName
                                  }`
                                )
                              : "-"}
                          </td>
                          <td className="text-center">
                            ${Number(record.requestAmount).toFixed(2)}
                          </td>
                          <td className="text-center">
                            ${Number(record.amountDiscount).toFixed(2)}
                          </td>
                          <td className="text-center">
                            {formatDate(record.startDayPay)}
                          </td>
                          <td className="text-center">
                            {formatDate(record.endDayPay)}
                          </td>
                          <td className="text-center">
                            <StatusBadge record={record} />
                          </td>

                          <td className="text-end">
                            <a
                              href="#/"
                              className="btn btn-azul m-2"
                              onClick={() =>
                                navigate(`/prestamos/editar/${record.id}`)
                              }
                            >
                              <i className="fa fa-eye me-1"></i>
                              Revisar
                            </a>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8}>
                          <div
                            className="py-4 justify-content-center align-items-center"
                            style={{ height: "450px" }}
                          >
                            <h5 className="text-secondary text-center fs-20px">
                              {error ? (
                                <div>
                                  <i className="fa fa-exclamation-triangle text-warning me-2"></i>
                                  Error al cargar préstamos: {error}
                                </div>
                              ) : (
                                "Aún no existen préstamos"
                              )}
                            </h5>
                            {!error && (
                              <>
                                <h5 className="text-center text-secondary fw-400 fs-15px">
                                  Prueba añadiendo uno en
                                </h5>
                                <h5 className="text-center text-secondary fw-400">
                                  el botón de 'Agregar préstamo'
                                </h5>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <PaginationLoans />
            </Panel>
          </div>
        </div>
      </div>

      <Loader isLoading={loading} />
    </Layout>
  );
};

export default AllLoans;
